import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

const LG_MEDIA = "(min-width: 992px)";
const RESIZE_DEBOUNCE_MS = 200;

/** Arc tuning — see comments below for what each value controls. */
const TUNING = {
	buttonLeftRatio: 0.25,
	buttonRightRatio: 0.75,
	leftWallHeightRatio: 0.26,
	loopDuration: 3.2,
	loopDelay: 2,
	rollRevolutions: 5,
	/**
	 * Entry/exit position (where the ball enters and leaves the panel).
	 * yFactor: negative = above the panel top; raise toward 0 or positive to lower.
	 * xFactor: distance off the right edge, in ball-size units.
	 */
	entryExit: {
		xFactor: 2,
		yFactor: -0.5,
	},
	/** Curve bulge only — does not move entry/exit points. */
	archHeight: {
		entryToButton: 0.14,
		buttonToWall: 0.38,
		wallToButton: 0.42,
		buttonToExit: 0.18,
	},
	buttonWobble: {
		squashY: 0.9,
		dipY: 4,
		tilt: 2,
	},
};

/**
 * Smooth symmetric arch between two points.
 * Control sits on the perpendicular bisector of the chord — a simple half-parabola.
 */
function archControl(from, to, heightFactor, peakUp) {
	const midX = (from.x + to.x) / 2;
	const midY = (from.y + to.y) / 2;
	const chord = Math.hypot(to.x - from.x, to.y - from.y);
	const rise = chord * heightFactor * (peakUp ? 1 : -1);

	return {
		x: midX,
		y: midY - rise,
	};
}

function compoundPath(segments) {
	return segments
		.map((segment, index) => {
			const { from, control, to } = segment;
			if (index === 0) {
				return `M${from.x},${from.y} Q${control.x},${control.y} ${to.x},${to.y}`;
			}
			return `Q${control.x},${control.y} ${to.x},${to.y}`;
		})
		.join(" ");
}

function sampleQuadraticLength(from, control, to, samples = 16) {
	let length = 0;
	let prev = from;

	for (let i = 1; i <= samples; i++) {
		const t = i / samples;
		const mt = 1 - t;
		const point = {
			x: mt * mt * from.x + 2 * mt * t * control.x + t * t * to.x,
			y: mt * mt * from.y + 2 * mt * t * control.y + t * t * to.y,
		};
		length += Math.hypot(point.x - prev.x, point.y - prev.y);
		prev = point;
	}

	return length;
}

function approximatePathLength(segments) {
	return segments.reduce(
		(total, segment) =>
			total + sampleQuadraticLength(segment.from, segment.control, segment.to),
		0
	);
}

function getBounceHitTimes(segments, pathLength, loopDuration) {
	const segmentLengths = segments.map((segment) =>
		sampleQuadraticLength(segment.from, segment.control, segment.to)
	);

	return [
		(segmentLengths[0] / pathLength) * loopDuration,
		((segmentLengths[0] + segmentLengths[1] + segmentLengths[2]) / pathLength) *
			loopDuration,
	];
}

function getBouncePoints(container, button) {
	const containerRect = container.getBoundingClientRect();
	const buttonRect = button.getBoundingClientRect();
	const ballSize = Math.min(containerRect.width, containerRect.height) * 0.12;
	const width = containerRect.width;
	const height = containerRect.height;
	const entryExitX = width + ballSize * TUNING.entryExit.xFactor;
	const entryExitY = ballSize * TUNING.entryExit.yFactor;

	const entry = {
		x: entryExitX,
		y: entryExitY,
	};
	const buttonLeft = {
		x: buttonRect.left - containerRect.left + buttonRect.width * TUNING.buttonLeftRatio,
		y: buttonRect.top - containerRect.top,
	};
	const leftWall = {
		x: 0,
		y: height * TUNING.leftWallHeightRatio,
	};
	const buttonRight = {
		x: buttonRect.left - containerRect.left + buttonRect.width * TUNING.buttonRightRatio,
		y: buttonRect.top - containerRect.top,
	};
	const exit = {
		x: entryExitX,
		y: entryExitY,
	};

	const segments = [
		{
			from: entry,
			to: buttonLeft,
			control: archControl(
				entry,
				buttonLeft,
				TUNING.archHeight.entryToButton,
				false
			),
		},
		{
			from: buttonLeft,
			to: leftWall,
			control: archControl(
				buttonLeft,
				leftWall,
				TUNING.archHeight.buttonToWall,
				true
			),
		},
		{
			from: leftWall,
			to: buttonRight,
			control: archControl(
				leftWall,
				buttonRight,
				TUNING.archHeight.wallToButton,
				true
			),
		},
		{
			from: buttonRight,
			to: exit,
			control: archControl(
				buttonRight,
				exit,
				TUNING.archHeight.buttonToExit,
				true
			),
		},
	];

	return {
		entry,
		path: compoundPath(segments),
		pathLength: approximatePathLength(segments),
		segments,
	};
}

export default class BlockCompetition {
	constructor(block) {
		this.block = block;
		this.textSection = this.block.querySelector(".block-competition-text-section");
		this.ball = this.block.querySelector(".beach-ball-illustration");
		this.button = this.block.querySelector(".btn.btn-night_blue");
		this.lgQuery = window.matchMedia(LG_MEDIA);
		this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
		this.timeline = null;
		this.scrollTrigger = null;
		this.resizeTimer = null;
		this.rollState = null;

		if (
			!this.textSection ||
			!this.ball ||
			!this.button ||
			this.reducedMotionQuery.matches ||
			!this.lgQuery.matches
		) {
			return;
		}

		gsap.set(this.ball, {
			xPercent: -50,
			yPercent: -50,
			transformOrigin: "50% 50%",
		});

		gsap.set(this.button, {
			transformOrigin: "50% 100%",
		});

		this.buildAnimation();
		this.lgQuery.addEventListener("change", this.onBreakpointChange);
		window.addEventListener("resize", this.onResize);
	}

	onBreakpointChange = (event) => {
		if (event.matches) {
			this.buildAnimation();
		} else {
			this.destroyAnimation();
			gsap.set(this.ball, { clearProps: "all" });
			gsap.killTweensOf(this.button);
			gsap.set(this.button, { clearProps: "transform" });
		}
	};

	onResize = () => {
		if (!this.lgQuery.matches || this.reducedMotionQuery.matches) {
			return;
		}
		clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(() => {
			this.buildAnimation();
		}, RESIZE_DEBOUNCE_MS);
	};

	destroyAnimation() {
		if (this.scrollTrigger) {
			this.scrollTrigger.kill();
			this.scrollTrigger = null;
		}
		if (this.timeline) {
			this.timeline.kill();
			this.timeline = null;
		}
		gsap.killTweensOf(this.button);
	}

	wobbleButton() {
		const { squashY, dipY, tilt } = TUNING.buttonWobble;

		gsap
			.timeline()
			.to(this.button, {
				scaleY: squashY,
				y: dipY,
				rotation: -tilt,
				duration: 0.07,
				ease: "power2.in",
				overwrite: "auto",
			})
			.to(this.button, {
				scaleY: 1.03,
				y: -1,
				rotation: tilt * 0.5,
				duration: 0.1,
				ease: "power2.out",
			})
			.to(this.button, {
				scaleY: 1,
				y: 0,
				rotation: 0,
				duration: 0.12,
				ease: "power2.out",
			});
	}

	buildAnimation() {
		this.destroyAnimation();

		const { entry, path, pathLength, segments } = getBouncePoints(
			this.textSection,
			this.button
		);
		const bounceHitTimes = getBounceHitTimes(
			segments,
			pathLength,
			TUNING.loopDuration
		);
		const degreesPerPixel = (TUNING.rollRevolutions * 360) / pathLength;
		const jumpThreshold = this.textSection.getBoundingClientRect().width * 0.4;

		this.rollState = {
			prevX: entry.x,
			prevY: entry.y,
			totalRotation: 0,
			travelSign: 1,
		};

		gsap.set(this.ball, {
			x: entry.x,
			y: entry.y,
			rotation: 0,
			opacity: 1,
		});

		gsap.set(this.button, {
			scaleY: 1,
			y: 0,
			rotation: 0,
		});

		this.timeline = gsap.timeline({
			repeat: -1,
			repeatDelay: TUNING.loopDelay,
			paused: true,
			scrollTrigger: {
				trigger: this.textSection,
				start: "top 85%",
				end: "bottom 15%",
				toggleActions: "play pause resume pause",
				invalidateOnRefresh: true,
			},
			onRepeat: () => {
				this.rollState.prevX = gsap.getProperty(this.ball, "x");
				this.rollState.prevY = gsap.getProperty(this.ball, "y");
			},
		});

		this.timeline.to(this.ball, {
			motionPath: {
				path,
			},
			duration: TUNING.loopDuration,
			ease: "none",
			onUpdate: () => {
				const x = gsap.getProperty(this.ball, "x");
				const y = gsap.getProperty(this.ball, "y");
				const dx = x - this.rollState.prevX;
				const dy = y - this.rollState.prevY;
				const dist = Math.hypot(dx, dy);

				if (dist > jumpThreshold) {
					this.rollState.prevX = x;
					this.rollState.prevY = y;
					return;
				}

				if (dist > 0.01) {
					if (Math.abs(dx) > 0.1) {
						this.rollState.travelSign = dx > 0 ? 1 : -1;
					}
					this.rollState.totalRotation +=
						dist * degreesPerPixel * this.rollState.travelSign;
					gsap.set(this.ball, { rotation: this.rollState.totalRotation });
				}

				this.rollState.prevX = x;
				this.rollState.prevY = y;
			},
		});

		bounceHitTimes.forEach((hitTime) => {
			this.timeline.call(() => this.wobbleButton(), null, hitTime);
		});

		this.scrollTrigger = this.timeline.scrollTrigger;
	}
}
